import { Component, inject, ViewChild } from "@angular/core";
import { MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { CardData } from "@models/credit-card";
import { finalize, take } from "rxjs";
import { ButtonComponent } from "../../../../shared/components/button/button.component";
import { DialogHeaderComponent } from "../../../../shared/components/dialog-header/dialog-header.component";
import { PaymentService } from "../../../services/payment.service";
import { SnackBarService } from "../../../services/snack-bar.service";
import {
  CreditCardComponent,
  CreditCardData,
} from "../../../shared/components/credit-card/credit-card.component";

interface SaveCardErrorPayload {
  success?: boolean;
  error?: string;
  message?: string;
  details?: {
    message?: string;
    errors?: Record<string, string[]>;
  };
}

@Component({
  selector: "app-add-card-dialog",
  imports: [
    MatDialogModule,
    ButtonComponent,
    CreditCardComponent,
    DialogHeaderComponent,
  ],
  templateUrl: "./add-card-dialog.component.html",
  styleUrl: "./add-card-dialog.component.scss",
})
export class AddCardDialogComponent {
  private readonly paymentService = inject(PaymentService);
  private readonly snackbarService = inject(SnackBarService);
  private readonly dialogRef = inject(MatDialogRef<AddCardDialogComponent>);

  @ViewChild(CreditCardComponent) private creditCard?: CreditCardComponent;

  saving = false;
  isCardFlipped = false;
  creditCardData: CreditCardData = {
    cardNumber: "",
    cardHolder: "",
    expiryMonth: "",
    expiryYear: "",
    cvv: "",
    brand: "Visa",
  };

  onCreditCardDataChange(data: CreditCardData): void {
    this.creditCardData = data;
  }

  onCardFlip(): void {
    this.isCardFlipped = !this.isCardFlipped;
  }

  onCvvFocus(): void {
    this.isCardFlipped = true;
  }

  handleCancelClick(): void {
    if (this.saving) return;
    this.dialogRef.close(false);
  }

  handleSaveClick(): void {
    // Bloqueia o envio com cartão inválido; força a exibição dos erros de blur.
    if (this.creditCard && !this.creditCard.isValid) {
      this.creditCard.markAllAsTouched();
      this.snackbarService.openSnackBar(
        "Verifique os dados do cartão.",
        "Fechar",
        "error",
      );
      return;
    }

    const cardData = this.buildCardData();
    if (!cardData) {
      this.snackbarService.openSnackBar(
        "Preencha todos os campos do cartão.",
        "Fechar",
        "error",
      );
      return;
    }

    this.saving = true;
    this.paymentService
      .saveCard(cardData)
      .pipe(
        take(1),
        finalize(() => {
          this.saving = false;
        }),
      )
      .subscribe({
        next: () => {
          this.snackbarService.openSnackBar(
            "Cartão salvo com sucesso.",
            "Fechar",
            "success",
          );
          this.dialogRef.close(true);
        },
        error: (err) => {
          this.snackbarService.openSnackBar(
            this.extractSaveCardErrorMessage(err),
            "Fechar",
            "error",
          );
        },
      });
  }

  private extractSaveCardErrorMessage(err: unknown): string {
    const fallback = "Não foi possível salvar o cartão.";
    const payload = (err as { error?: SaveCardErrorPayload })?.error;
    if (!payload) return fallback;

    const errorsObj = payload.details?.errors;
    if (errorsObj) {
      const messages = Object.values(errorsObj)
        .flat()
        .filter((m): m is string => typeof m === "string" && m.length > 0)
        .map((m) => this.translateCardError(m));
      if (messages.length) return messages.join(" ");
    }

    const raw =
      payload.details?.message || payload.error || payload.message || "";
    return raw ? this.translateCardError(raw) : fallback;
  }

  private translateCardError(raw: string): string {
    const normalized = raw.toLowerCase().trim();

    if (
      normalized.includes("not a valid card number") ||
      normalized.includes("invalid card number")
    ) {
      return "Número de cartão inválido.";
    }
    if (normalized.includes("could not detect card brand")) {
      return "Não foi possível identificar a bandeira do cartão.";
    }
    if (normalized === "the request is invalid.") {
      return "Verifique os dados do cartão e tente novamente.";
    }
    if (normalized === "failed to add card") {
      return "Não foi possível adicionar o cartão.";
    }

    return raw;
  }

  private buildCardData(): CardData | null {
    const data = this.creditCardData;
    const number = (data.cardNumber || "").replace(/\s/g, "");
    const holder = (data.cardHolder || "").trim();
    const month = String(data.expiryMonth || "").padStart(2, "0");
    const rawYear = String(data.expiryYear || "");
    const year =
      rawYear.length === 2
        ? String(new Date().getFullYear()).slice(0, 2) + rawYear
        : rawYear;
    const cvv = String(data.cvv || "");

    if (!number || !holder || !month || !year || !cvv) return null;

    return {
      card_number: number,
      card_holder_name: holder,
      card_expiration_date: `${month}${year.slice(-2)}`,
      card_cvv: cvv,
    };
  }
}
